import { API_BASE_URL, API_TAG_TYPES, API_VERSION } from 'constants/common';
import { Permission, TeamRole } from 'containers/app/app.model';
import { baseApi } from 'services/api';
import { getUrlSearchParams } from 'utils/apiUtils';
import { initializeEditPermissionsSliceFromAPI } from './edit-permissions/editPermissionsSlice';
import {
  CreateInvitePayload,
  DeleteCommissionRulePayload,
  EditTeamMemberPermissionsPayload,
  GetInvitedMemberProfile,
  GetTeamMemberPermissions,
  GetTeamMemberPermissionsResponse,
  GetTeamMembersPayload,
  GetTeamMembersResponse,
  IGetTeamMembers,
  InviteLinkPayload,
  MemberActivationPayload,
  TeamInviteToken,
  TeamMemberById,
  UpdateMerchantColorPayload
} from './manageTeam.model';
import { getPermissions } from 'containers/app/util';

const TeamMembersBaseApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getTeamMembers: builder.query<IGetTeamMembers, GetTeamMembersPayload>({
      query: ({ teamId, ...params }) => {
        let url = `${API_VERSION.V1}/merchants/teams/${teamId}/members?`;
        const urlParams = getUrlSearchParams(params);
        url = url + urlParams;
        return {
          url: url,
          method: 'GET'
        };
      },
      transformResponse: (response: GetTeamMembersResponse) => {
        return { id: response.id, teamMembers: response.members, totalCount: response.totalMembers };
      },
      extraOptions: {
        failure: 'Failed to fetch manage teams data',
        showToast: true
      }
    }),

    getTeamMemberPermissions: builder.query<GetTeamMemberPermissionsResponse, GetTeamMemberPermissions>({
      query: ({ roleId, teamId }) => {
        const url = `${API_VERSION.V2}/team/${teamId}/permissions/${roleId}`;
        return {
          url: url,
          method: 'GET'
        };
      },

      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        const editPermissionSliceStateFromAPI: Permission = {
          role: data.role,
          permissions: data.role === TeamRole.CUSTOM ? data.permissions : getPermissions(data.role).permissions
        };
        dispatch(initializeEditPermissionsSliceFromAPI(editPermissionSliceStateFromAPI));
      },
      extraOptions: {
        failure: 'Failed to fetch manage teams data',
        showToast: true
      }
    }),
    createInvite: builder.mutation<any, CreateInvitePayload>({
      query: ({ teamId, payload }) => {
        return {
          url: `${API_VERSION.V2}/team-invites/invite/${teamId}`,
          method: 'POST',
          body: payload
        };
      }
    }),
    getTeamMemberById: builder.query<TeamMemberById, MemberActivationPayload>({
      query: params => {
        const { merchantId, teamId } = params;
        return {
          url: `${API_BASE_URL.WEB_APP}/${API_VERSION.V1}/team-members/${merchantId}?teamId=${teamId}`,
          method: 'GET'
        };
      },

      extraOptions: {
        failure: 'Failed to fetch team member data'
      }
    }),

    editPermissions: builder.mutation<any, EditTeamMemberPermissionsPayload>({
      query: payload => {
        return {
          url: `${API_VERSION.V2}/permissions/${payload.teamId}/${payload.merchantId}`,
          method: 'PUT',
          body: {
            role: payload.role,
            permissions: payload.permissions
          }
        };
      },
      invalidatesTags: [API_TAG_TYPES.TEAM_MEMBER_DETAILS],
      extraOptions: {
        failure: 'Failed to update permissions',
        success: 'Permissions changed successfully',
        showToast: true
      }
    }),
    deactivateMember: builder.mutation<any, MemberActivationPayload>({
      query: ({ merchantId, teamId }) => {
        return {
          url: `${API_VERSION.V1}/team-members/${teamId}/${merchantId}`,
          method: 'DELETE'
        };
      },
      invalidatesTags: [API_TAG_TYPES.TEAM_MEMBER_DETAILS],
      extraOptions: {
        failure: 'Deactivation failed',
        success: 'Member deactivated successfully',
        showToast: true
      }
    }),
    reactivateMember: builder.mutation<any, MemberActivationPayload>({
      query: ({ merchantId, teamId }) => {
        return {
          url: `${API_VERSION.V1}/team-members/${teamId}/${merchantId}`,
          method: 'POST',
          body: { teamId: teamId.toString() }
        };
      },
      invalidatesTags: [API_TAG_TYPES.TEAM_MEMBER_DETAILS],
      extraOptions: {
        failure: 'Reactivation failed',
        success: 'Member reactivated successfully',
        showToast: true
      }
    }),
    shareInviteLink: builder.mutation<any, InviteLinkPayload>({
      query: ({ inviteId, updateMerchantInformation = false, email = '', phoneNumber = '', teamId }) => {
        return {
          url: `${API_VERSION.V2}/team-invites/resend/${inviteId}`,
          method: 'POST',
          body: { updateMerchantInformation, email, phoneNumber, teamId }
        };
      },
      extraOptions: {
        failure: 'Invite link sharing failed',
        success: 'Invite link shared successfully',
        showToast: true
      }
    }),
    deleteInvite: builder.mutation<any, InviteLinkPayload>({
      query: ({ inviteId, teamId }) => {
        return {
          url: `${API_VERSION.V2}/team/${teamId}/team-invites/${inviteId}`,
          method: 'DELETE'
        };
      },
      extraOptions: {
        failure: 'Failed to delete invite',
        success: 'Invite link deleted successfully',
        showToast: true
      }
    }),

    acceptTeamInvite: builder.mutation<any, TeamInviteToken>({
      query: payload => {
        return {
          url: `${API_VERSION.V1}/team-invites/accept`,
          method: 'POST',
          body: payload
        };
      },
      extraOptions: {
        failure: 'Invalid invite link and you are redirected to your previous logged in account',
        success: 'Invite accepted successfully',
        showToast: true
      }
    }),
    declineTeamInvite: builder.mutation<any, TeamInviteToken>({
      query: payload => {
        return {
          url: `${API_VERSION.V1}/team-invites/reject`,
          method: 'POST',
          body: payload
        };
      },
      extraOptions: {
        failure: 'Failed to decline invite',
        success: 'Invite declined successfully',
        showToast: true
      }
    }),

    getTeamMemberDetails: builder.query<TeamMemberById, { teamId: number; merchantId: string }>({
      query: ({ teamId, merchantId }) => {
        return {
          url: `${API_VERSION.V1}/merchants/teams/${teamId}/members/${merchantId}`,
          method: 'GET'
        };
      },
      providesTags: [API_TAG_TYPES.TEAM_MEMBER_DETAILS],
      extraOptions: {
        failure: 'Failed to fetch team member details',
        showToast: true
      }
    }),

    updateMerchantColor: builder.mutation<void, UpdateMerchantColorPayload>({
      query: ({ teamId, ...payload }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/update-color`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: [API_TAG_TYPES.TEAM_MEMBER_DETAILS],
      extraOptions: {
        failure: 'Failed to update merchant color',
        success: 'Merchant color updated successfully',
        showToast: true
      }
    }),

    updateMemberServiceAreas: builder.mutation<void, { teamId: number; merchantId: string; serviceAreaIds: string[] }>({
      query: ({ teamId, merchantId, serviceAreaIds }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/merchants/${merchantId}/service-areas`,
        method: 'PUT',
        body: { serviceAreaIds }
      }),
      invalidatesTags: [API_TAG_TYPES.TEAM_MEMBER_DETAILS],
      extraOptions: {
        failure: 'Failed to update service areas',
        success: 'Service areas updated successfully',
        showToast: true
      }
    }),

    removeCommissionRuleForMember: builder.mutation<void, DeleteCommissionRulePayload>({
      query: ({ teamId, merchantId, ruleId }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/member/${merchantId}/commission-rule/${ruleId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [API_TAG_TYPES.TEAM_MEMBER_DETAILS],
      extraOptions: {
        failure: 'Failed to delete commission rule',
        success: 'Commission rule deleted successfully',
        showToast: true
      }
    }),

    getInvitedMemberProfile: builder.query<GetInvitedMemberProfile, { teamId: number; phoneNumber: string }>({
      query: ({ teamId, phoneNumber }) => ({
        url: `${API_VERSION.V1}/merchants/teams/${teamId}/invite/${phoneNumber}`,
        method: 'GET'
      }),
      extraOptions: {
        failure: 'Failed to fetch invited member profile',
        showToast: true
      }
    })
  })
});

export const {
  useLazyGetTeamMembersQuery,
  useGetTeamMemberPermissionsQuery,
  useCreateInviteMutation,
  useLazyGetTeamMemberByIdQuery,
  useDeactivateMemberMutation,
  useReactivateMemberMutation,
  useShareInviteLinkMutation,
  useDeleteInviteMutation,
  useEditPermissionsMutation,
  useAcceptTeamInviteMutation,
  useDeclineTeamInviteMutation,
  useLazyGetTeamMemberDetailsQuery,
  useUpdateMerchantColorMutation,
  useUpdateMemberServiceAreasMutation,
  useRemoveCommissionRuleForMemberMutation,
  useLazyGetInvitedMemberProfileQuery
} = TeamMembersBaseApi;
